import React, { useState, useRef } from 'react';
import { FiMapPin, FiPhone, FiMail } from 'react-icons/fi';
import { BiTimeFive } from 'react-icons/bi';
import ReCAPTCHA from 'react-google-recaptcha';
import { API_BASE_URL } from '../utils/api';

function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false); // New state for sending status
  const recaptchaRef = useRef();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.phone) newErrors.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(formData.phone))
      newErrors.phone = 'Enter a valid 10-digit phone number';
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Enter a valid email';
    if (!formData.subject) newErrors.subject = 'Subject is required';
    if (!formData.message) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSending(true); // Set sending status to true
    setErrors({}); // Clear previous errors

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        console.log('Form submitted successfully');
        setFormData({ name: '', phone: '', email: '', subject: '', message: '' });
        recaptchaRef.current.reset(); // Uncomment if using reCAPTCHA
      } else {
        console.error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSending(false); // Set sending status back to false
    }
  };

  return (
    <div className='w-full bg-white px-4 py-12 md:px-16'>
      <div className='grid gap-8 md:grid-cols-2'>
        {/* Left Side - Form */}
        <div className='rounded-lg border border-gray-200 bg-white p-8 shadow'>
          <form
            className='space-y-5'
            onSubmit={handleSubmit}
          >
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <div className='flex flex-col'>
                <label
                  htmlFor='name'
                  className='mb-1 text-sm font-medium text-gray-700'
                >
                  Name
                </label>
                <input
                  type='text'
                  name='name'
                  id='name'
                  className='w-full rounded border border-gray-300 px-4 py-3'
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isSending} // Disable input fields during submission
                />
                {errors.name && <p className='mt-1 text-sm text-red-500'>{errors.name}</p>}
              </div>

              <div className='flex flex-col'>
                <label
                  htmlFor='phone'
                  className='mb-1 text-sm font-medium text-gray-700'
                >
                  Phone
                </label>
                <input
                  type='tel'
                  name='phone'
                  id='phone'
                  className='w-full rounded border border-gray-300 px-4 py-3'
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={isSending}
                />
                {errors.phone && <p className='mt-1 text-sm text-red-500'>{errors.phone}</p>}
              </div>
            </div>

            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <div className='flex flex-col'>
                <label
                  htmlFor='email'
                  className='mb-1 text-sm font-medium text-gray-700'
                >
                  Email
                </label>
                <input
                  type='email'
                  name='email'
                  id='email'
                  className='w-full rounded border border-gray-300 px-4 py-3'
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSending}
                />
                {errors.email && <p className='mt-1 text-sm text-red-500'>{errors.email}</p>}
              </div>

              <div className='flex flex-col'>
                <label
                  htmlFor='subject'
                  className='mb-1 text-sm font-medium text-gray-700'
                >
                  Subject
                </label>
                <input
                  type='text'
                  name='subject'
                  id='subject'
                  className='w-full rounded border border-gray-300 px-4 py-3'
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={isSending}
                />
                {errors.subject && <p className='mt-1 text-sm text-red-500'>{errors.subject}</p>}
              </div>
            </div>

            <div className='flex flex-col'>
              <label
                htmlFor='message'
                className='mb-1 text-sm font-medium text-gray-700'
              >
                Message
              </label>
              <textarea
                rows='4'
                name='message'
                id='message'
                className='w-full rounded border border-gray-300 px-4 py-3'
                value={formData.message}
                onChange={handleChange}
                disabled={isSending}
              ></textarea>
              {errors.message && <p className='mt-1 text-sm text-red-500'>{errors.message}</p>}
            </div>

            {/* Re-add reCAPTCHA if you need it */}
            <div>
              <ReCAPTCHA
                sitekey='6LcmktYrAAAAAEg0JVdKaKO_s9cqoQQP2QI5iiRs'
                ref={recaptchaRef}
                onChange={(value) => setRecaptchaValue(value)}
              />
              {errors.recaptcha && <p className='mt-2 text-sm text-red-500'>{errors.recaptcha}</p>}
            </div>

            <button
              type='submit'
              className='relative inline-flex h-[46px] w-full items-center justify-center rounded-[6px] bg-gradient-to-r from-[#495BFF] to-[#55E6A5] p-[1px] disabled:opacity-50'
              disabled={isSending} // Disable the button while sending
            >
              <span className='flex h-full w-full items-center justify-center rounded-lg bg-transparent text-[16px] font-bold leading-tight text-white'>
                {isSending ? (
                  <span>
                    Sending<span className='animate-pulse-dots'>.</span>
                    <span className='animate-pulse-dots delay-100'>.</span>
                    <span className='animate-pulse-dots delay-200'>.</span>
                  </span>
                ) : (
                  'Send Message'
                )}
              </span>
            </button>
          </form>
        </div>

        {/* Right Side - Contact Info */}
        <div className='flex h-full flex-col gap-6'>
          <div className='flex h-full flex-col gap-6'>
            <div className='flex-1 rounded-lg border border-gray-200 bg-white p-8 shadow'>
              <h2 className='mb-6 text-xl font-semibold'>Contact Information</h2>
              <div className='space-y-4'>
                {/* Phone 1 */}
                <div className='flex items-center gap-3'>
                  <div className='rounded-full bg-gradient-to-r from-[#495BFF] to-[#55E6A5] p-3 text-white'>
                    <FiPhone className='text-xl' />
                  </div>
                  <div>
                    <a
                      href='tel:+919429620999'
                      className='text-gray-700 transition hover:text-[#495BFF]'
                    >
                      (+91) 9429620999
                    </a>
                  </div>
                </div>

                {/* Phone 2 */}
                <div className='flex items-center gap-3'>
                  <div className='rounded-full bg-gradient-to-r from-[#495BFF] to-[#55E6A5] p-3 text-white'>
                    <FiPhone className='text-xl' />
                  </div>
                  <div>
                    <a
                      href='tel:+917573035965'
                      className='text-gray-700 transition hover:text-[#495BFF]'
                    >
                      (+91) 7573035965
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className='flex items-center gap-3'>
                  <div className='rounded-full bg-gradient-to-r from-[#495BFF] to-[#55E6A5] p-3 text-white'>
                    <FiMail className='text-xl' />
                  </div>
                  <a
                    href='mailto:info@bluelagoontravels.com'
                    className='text-gray-700 transition hover:text-[#495BFF]'
                  >
                    info@bluelagoontravels.com
                  </a>
                </div>

                {/* Address */}
                <div className='flex items-center gap-3'>
                  <div className='rounded-full bg-gradient-to-r from-[#495BFF] to-[#55E6A5] p-3 text-white'>
                    <FiMapPin className='text-xl' />
                  </div>
                  <a
                    href='https://maps.app.goo.gl/zn751N8wUJWLWVE89'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-gray-700 transition hover:text-[#495BFF]'
                  >
                    B-5, Madhav Apartment, Near Vasna Busstand, Vasna Ahmedabad-380007
                  </a>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className='flex-1 rounded-lg border border-gray-200 bg-white p-8 shadow'>
              <h2 className='mb-6 text-xl font-semibold'>Business Hours</h2>
              <div className='flex items-center gap-3'>
                <div className='rounded-full bg-gradient-to-r from-[#495BFF] to-[#55E6A5] p-3 text-white'>
                  <BiTimeFive className='text-xl' />
                </div>
                <p className='text-gray-700'>7 Days - 10:00 am to 7:00 pm</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactUs;
